import z from 'zod';
import { ETemplateStyles } from '../../types';
import { wrapError, wrapSuccess } from '../../types/responses';
import { cdnIfy, unCdnify } from '../../helpers';

const getFillerWidthInPixels = async (
  columns: number,
  templateStyles: ETemplateStyles
) => {
  if (columns < 1) {
    return wrapError(new Error('Invalid number of columns'));
  }
  if (
    !templateStyles.pageWidth ||
    !templateStyles.columnGutter ||
    !templateStyles.columnCount
  ) {
    return wrapError(new Error('Invalid template styles'));
  }

  const totalGutterWidth =
    templateStyles.columnGutter * (templateStyles.columnCount - 1);

  const pageWidthWithoutGutter = templateStyles.pageWidth - totalGutterWidth;

  const columnWidth = pageWidthWithoutGutter / templateStyles.columnCount;

  const adWidthFromColumns = columnWidth * columns;
  const adWidthFromGutter = templateStyles.columnGutter * (columns - 1);

  const adWidth = adWidthFromColumns + adWidthFromGutter;
  return wrapSuccess(adWidth);
};

const getSizeURL = (pdfUrl: string) => {
  if (pdfUrl.includes('imgix.net')) {
    return `${pdfUrl}?fm=json`;
  }
  return cdnIfy(unCdnify(pdfUrl), {
    useImgix: true,
    imgixTransformations: {
      fm: 'json'
    }
  });
};

const getFillerSize = async (pdfUrl: string) => {
  const fillerJpgUrl = getSizeURL(pdfUrl);
  const sizeResponse = await fetch(fillerJpgUrl);
  let sizeJson;
  try {
    sizeJson = await sizeResponse.json();
  } catch (error) {
    return wrapError(
      new Error(
        `Invalid size json: ${JSON.stringify(error)} from ${fillerJpgUrl}`
      )
    );
  }
  const { success, data, error } = z
    .object({
      PixelWidth: z.number(),
      PixelHeight: z.number()
    })
    .safeParse(sizeJson);
  if (!success) {
    return wrapError(new Error(`Invalid size json: ${JSON.stringify(error)}`));
  }
  return wrapSuccess({
    imgWidth: data.PixelWidth,
    imgHeight: data.PixelHeight
  });
};

export const getFillerHeightInPixels = async (
  url: string,
  columnWidth: number,
  templateStyles: ETemplateStyles
) => {
  const [widthError, width] = await getFillerWidthInPixels(
    columnWidth,
    templateStyles
  );
  if (widthError) {
    return wrapError(widthError);
  }
  const [fillerSizeError, fillerSize] = await getFillerSize(url);
  if (fillerSizeError) {
    return wrapError(fillerSizeError);
  }
  const fillerHeight = Math.round(
    (fillerSize.imgHeight * width) / fillerSize.imgWidth
  );
  return wrapSuccess(fillerHeight);
};
